import React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faComment } from '@fortawesome/free-solid-svg-icons/faComment';
import { faQuestion } from '@fortawesome/free-solid-svg-icons/faQuestion';
import { faChartBar } from '@fortawesome/free-solid-svg-icons/faChartBar';
import { faMobile } from '@fortawesome/free-solid-svg-icons/faMobile';
import { faCog } from '@fortawesome/free-solid-svg-icons/faCog';

import { Chat, FullScreen, Poll, Questions, Reactions, RemoteControl, Stream, UserSettings } from 'Components/MainStage';
import { PrimaryButton } from 'Components/Button';

import { translation } from 'Services/TranslationHelpers';
import { roundedElements } from 'Services/BaseHelpers';
import Link from "Components/Utilities/Link";
import { User } from "Services";

export default class Tabs extends React.Component {
    /**
     * @var videoContainerRef
     * @type HTMLDivElement
     */
    videoContainerRef = null;

    /**
     * @constructor
     * @param {object} props
     */
    constructor(props) {
        super(props);

        this.state = {
            tab: null,
            height: 0,
            streamType: null,
            hideVideo: false,
        };

        this.videoContainerRef = React.createRef();
    }

    /**
     * @method componentDidMount
     */
    componentDidMount = () => {
        window.addEventListener('resize', this.resizeListener);
    };

    /**
     * @method componentDidUpdate
     * @param {object} prevProps
     */
    componentDidUpdate = (prevProps) => {
        if (this.props.currentSession?.polls_enabled && prevProps.currentPoll?.id !== this.props.currentPoll?.id) {
            this.setState({
                tab: 'polls',
            });
        }
    };

    /**
     * @method componentWillUnmount
     */
    componentWillUnmount = () => {
        window.removeEventListener('resize', this.resizeListener);
    };

    /**
     * @method resizeListener
     */
    resizeListener = () => this.setState({
        height: this.videoContainerRef?.clientHeight ?? this.state.height,
    });

    /**
     * @method handleCreateRef
     * @param {HTMLDivElement} ref
     */
    handleCreateRef = (ref) => {
        this.videoContainerRef = ref;

        this.resizeListener();
    };

    /**
     * @method handleSetTab
     * @param {string} tab
     */
    handleSetTab = (tab) => this.setState({
        tab,
    });

    /**
     * @method getTabsCount
     * @return {array}
     */
    getTabsCount = () => {
        const { currentSession } = this.props;

        let count = 0;

        if (currentSession?.chat) {
            count++;
        }

        if (currentSession?.questions) {
            count++;
        }

        if (currentSession?.polls_enabled) {
            count++;
        }

        if (currentSession?.remote_control) {
            count++;
        }

        if (currentSession?.user_settings) {
            count++;
        }

        return count;
    }

    /**
     * @method render
     * @return {JSX.Element}
     */
    render () {
        const { tab, streamType } = this.state;
        const { currentSession, userSettings } = this.props;

        if (!currentSession) {
            return (
                <div className="my-10 text-center text-xl text-app-blue font-bold">
                    {translation('main_stage_frontend', 'no_sessions_live')}
                </div>
            );
        }

        if (currentSession.outage_message) {
            return (
                <div className="my-10 text-center text-6xl font-bold text-app-blue">
                    {currentSession.outage_message}
                </div>
            );
        }

        let tabsCount = this.getTabsCount();
        let display_interactions = tabsCount > 0;
        let display_tabs_menu = tabsCount > 1;
        let current_tab = tab;

        if (!current_tab) {
            current_tab = currentSession?.chat ? 'chat' :
                currentSession?.questions ? 'questions' :
                currentSession?.polls_enabled ? 'polls' :
                currentSession?.remote_control ? 'remote_control' :
                currentSession?.user_settings ? 'user_settings' : null;
        }

        return (
            <div className={"max-w-7xl mx-auto px-4"}>
                <div className="grid grid-cols-6 md:grid-cols-12 gap-6 my-10">
                    <div
                        className={`
                            col-span-6
                            ${display_interactions ? (display_tabs_menu ? 'md:col-span-7' : 'md:col-span-8') : 'md:col-span-12'}
                        `}
                        ref={this.handleCreateRef}
                    >

                        {/*<h1 className="mb-2 text-2xl font-bold text-app-blue">{currentSession.title}</h1>*/}

                        {/*<FullScreen>*/}
                            {currentSession?.reactions === true && userSettings?.reactions === true ? (
                                <Reactions
                                    side="right"
                                    sessionId={currentSession.id}
                                >
                                    <Stream
                                        stream={currentSession}
                                        streamType={streamType}
                                        setStreamType={(type) => this.setState({ streamType: type })}
                                    />
                                </Reactions>
                            ) : (
                                <div className="relative w-full">
                                    <Stream
                                        stream={currentSession}
                                        streamType={streamType}
                                        setStreamType={(type) => this.setState({ streamType: type })}
                                    />
                                </div>
                            )}
                        {/*</FullScreen>*/}
                    </div>

                    {display_interactions &&
                        <>
                            {display_tabs_menu &&
                                <div className={`flex flex-col bg-app-blue max-h-[45em] md:max-h-[35em] ${this.state.hideVideo ? 'mx-auto col-span-12 w-full' : 'col-span-6 md:col-span-5'} rounded-xl`}>
                                    <div className={`text-white flex flex-row ${roundedElements() ? 'rounded-xl ' : ''}`}>
                                        {currentSession?.chat && this.renderTabButton('chat', 'Chat', faComment, current_tab)}
                                        {currentSession?.questions && this.renderTabButton('questions', 'Q&A', faQuestion, current_tab)}
                                        {currentSession?.polls_enabled && this.renderTabButton('polls', 'Polls', faChartBar, current_tab)}
                                        {currentSession?.remote_control && this.renderTabButton('remote_control', 'Remote Control', faMobile, current_tab)}
                                        {currentSession?.user_settings && this.renderTabButton('user_settings', 'Settings', faCog, current_tab)}
                                    </div>

                                    {this.renderTab(current_tab)}
                                </div>
                            }
                        </>
                    }
                </div>
            </div>
        );
    }

    /**
     * @method renderTabButton
     * @param {string} tab
     * @param {string} title
     * @param {IconDefinition} icon
     * @param {string} current_tab
     * @returns {JSX.Element}
     */
    renderTabButton = (tab, title, icon, current_tab) => (
        <div
            className={`${current_tab === tab ? 'border-b-8 border-app-light-blue' : 'hover:border-b-8 hover:border-app-light-blue  cursor-pointer'} w-full flex items-center justify-center h-12 transition duration-200`}
            onClick={() => this.handleSetTab(tab)}
            title={title}
        >
            {/*<FontAwesomeIcon*/}
            {/*    icon={icon}*/}
            {/*    size="lg"*/}
            {/*    className="text-white"*/}
            {/*/>*/}
            <p className="text-white text-md font-bold text-center">{title}</p>
        </div>
    );

    /**
     * @method renderTab
     * @param {string} current_tab
     * @return {JSX.Element}
     */
    renderTab = (current_tab) => {
        const { height } = this.state;
        const { currentSession, currentPoll } = this.props;

        switch (current_tab) {
            case 'chat':
                return (
                    <Chat
                        sessionId={currentSession.id}
                        height={height}
                    />
                );
            case 'questions':
                return (
                    <Questions
                        sessionId={currentSession.id}
                        height={height}
                    />
                );
            case 'polls':
                return (
                    <Poll
                        poll={currentPoll}
                        sessionId={currentSession.id}
                        height={height}
                        onSuccess={() => this.handleSetTab('chat')}
                    />
                );
            case 'remote_control':
                return (
                    <RemoteControl
                        sessionId={currentSession.id}
                        height={height}
                    />
                );
            case 'user_settings':
                return (
                    <UserSettings
                        height={height}
                    />
                );
        }
    }
}
